<template>
  <b-card class="filter">
    <h3 class="pb-4">Filter messages</h3>

    <div class="d-flex pb-3 status-filter">
      <div v-for="{ value, text } in statusesData" :key="String(value)" class="pr-4">
        <div class="form-check">
          <input :id="'status-' + value" v-model="statuses" class="form-check-input" type="checkbox" :value="value" />
          <label class="form-check-label" :for="'status-' + value">
            {{ text }}
          </label>
        </div>
      </div>
    </div>

    <div class="d-flex pb-3">
      <div v-for="carrierHandle in carriersData" :key="carrierHandle" class="pr-4">
        <div class="form-check">
          <input
            :id="'carrier-' + carrierHandle"
            v-model="carriers"
            class="form-check-input"
            type="checkbox"
            :value="carrierHandle"
          />
          <label class="form-check-label" :for="'carrier-' + carrierHandle">
            <CarrierRowWithIcon :carrier="carrierHandle" />
          </label>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-3">
        <div class="d-flex text-input-block">
          <input v-model="shipmentNumber" class="form-control" type="text" placeholder="Shipment number" />
        </div>
      </div>
      <div class="col-3 date-block">
        <VueDatePicker
          v-model="sentAt"
          format="yyyy-MM-dd"
          range
          multi-calendars
          placeholder="Sent at"
          :clearable="true"
        >
        </VueDatePicker>
      </div>
      <div class="col-3">
        <div class="d-flex text-input-block">
          <input v-model="country" class="form-control" type="text" placeholder="Country Code" />
        </div>
      </div>
      <div class="col-3 text-right">
        <b-button @click="submit"> Submit </b-button>
        <b-button v-if="hasFilters" class="ml-2" variant="secondary" @click="reset"> Reset </b-button>
      </div>
    </div>
  </b-card>
</template>

<script lang="ts">
import type { ShipmentsQueryParams } from '@/types/Components/SearchForm'
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { ComputedRef, Ref } from 'vue'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { BButton, BCard } from 'bootstrap-vue-next'
import { ref, computed, watch, onMounted, defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CARRIERS } from '@/const/Carriers'
import { PROXY_MESSAGES } from '@/types/router'
import moment from 'moment'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'

export default defineComponent({
  name: 'MessagesSearchForm',
  components: { BButton, BCard, VueDatePicker, CarrierRowWithIcon },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const statuses: Ref<string[]> = ref([])
    const carriers: Ref<string[]> = ref([])
    const shipmentNumber: Ref<string | null> = ref(null)
    const country: Ref<string | null> = ref(null)
    const sentAt: Ref<string[] | null> = ref(null)
    const carriersData = CARRIERS
    const routeHandle = PROXY_MESSAGES

    const statusesData: SelectOptions[] = [
      { value: 'success', text: 'Success' },
      { value: 'failure', text: 'Failure' },
      { value: 'not_eligible', text: 'Not eligible' },
      { value: 'assignment_archived', text: 'Assignment archived' },
      { value: 'delivered', text: 'Delivered' },
      { value: 'opened', text: 'Opened' },
    ]

    const hasFilters: ComputedRef<boolean> = computed(() =>
      Boolean(statuses.value.length || shipmentNumber.value || carriers.value.length || sentAt.value),
    )

    const submit = () => {
      const query = {} as ShipmentsQueryParams

      query.country = country.value ? String(country.value) : undefined
      query.shipmentNumber = shipmentNumber.value ? String(shipmentNumber.value) : undefined
      query.statuses = statuses.value.length ? statuses.value.map(String).join(',') : undefined
      query.carriers = carriers.value.length ? carriers.value.map(String).join(',') : undefined

      if (sentAt.value && Array.isArray(sentAt.value) && sentAt.value.length === 2) {
        const [dateFrom, dateTo] = sentAt.value
        query.dateFrom = moment(dateFrom).format('YYYY-MM-DD')
        query.dateTo = moment(dateTo).format('YYYY-MM-DD')
      }

      router.push({ name: routeHandle, query })
    }

    const reset = () => {
      router.push({ name: routeHandle })
    }

    watch(sentAt, submit)

    onMounted(() => {
      const routeQuery = route.query
      statuses.value = routeQuery.statuses ? String(routeQuery.statuses).split(',') : []
      carriers.value = routeQuery.carriers ? String(routeQuery.carriers).split(',') : []
      shipmentNumber.value = routeQuery.shipmentNumber ? String(routeQuery.shipmentNumber) : null
      country.value = routeQuery.country ? String(routeQuery.country) : null

      if (routeQuery.dateFrom && routeQuery.dateTo) {
        sentAt.value = [String(routeQuery.dateFrom), String(routeQuery.dateTo)]
      }
    })

    return {
      sentAt,
      country,
      statuses,
      carriers,
      routeHandle,
      carriersData,
      statusesData,
      shipmentNumber,
      hasFilters,
      submit,
      reset,
    }
  },
})
</script>
